/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable array-callback-return */
import { TAG_CONF } from '@/config';
import { i18n } from '@/i18n';
import { CouponState, ViewCouponConf } from '@/types/coupon';
import { CouponButtonType, CouponTagColor, CouponTipsType } from '@tencent/wphk-act-base-ui';
import dayjs from 'dayjs';
import { ExActAndConfigCouponInfo } from '../types/coupon';
import { formatPercent } from './index';
import { CouponType, ExpireType } from '@tencent/wphk-act-base-cgi';
/**
 * 是否是日限额的券
 * @param execAct
 * @returns
 */
const isDayLimitCoupon = (execAct) => {
  const dayLimit = execAct.dayLimit || 99999999;
  const daySent = execAct.daySent || 0;
  return !(dayLimit === 99999999 && daySent === 0);
};

/** 是否商户展示券 */
const checkIsSpCoupon = (couponType: CouponType) => {
  // 三种商户展示券类型
  const isSpCoupon = [
    CouponType.SPCOUOPN,
    CouponType.SPCOUOPN_CDKEY,
    CouponType.SPCOUOPN_ONECDKEY,
  ].includes(couponType);
  return isSpCoupon;
};

/** 是否是折扣券 */
const checkIsDiscountCoupon = (couponType: CouponType) => couponType === CouponType.DISC;

/** 格式券金额 */
const getFormattedDiscount = (couponDiscount: number, couponType: CouponType, lang) => {
  const discount = Number(couponDiscount);
  if (couponType !== CouponType.DISC) {
    return discount;
  }
  if (lang === 'en') {
    return discount;
  }
  return (100 - discount) / 10;
};

/**
 * 获取优惠券余量百分比
 */
const getAvailablePercent = (execAct) => {
  // totalNum 兑换活动最大参与总次数（所有用户）
  // totalSendNum 兑换活动已参与总次数（所有用户）
  const { totalNum, totalSendNum } = execAct;
  // 平台当日兑换上限次数
  const dayLimit = execAct.dayLimit || 99999999;
  // 平台当日已兑换次数
  const daySent = execAct.daySent || 0;

  if (isDayLimitCoupon(execAct)) {
    // 有日限额的券
    return formatPercent(daySent, dayLimit);
  }
  // 没有日限额的券
  return formatPercent(totalSendNum, totalNum);
};

/**
 * 解析优惠券信息，领取状态
 *
 * over > pover > unable > enable(个人已达上限》平台今日已领完》印花不足》可领取)
 *
 * end 没有
 * 非日限额消耗： 某一优惠券总库存消耗完（非日限额消耗完）时，该优惠券下线，不在页面显示
 * 日限额： 没有已兑罄，我们一般会每日均摊，库存消耗完的那一天就是优惠券下线的时间，所以没有已兑罄状态(日限额当天就是pover，最后是end)
 *
 * expired 没有
 *
 * lock 没有
 *
 * @param {Object} coupon 券
 * @param {Object} execAct 兑换活动
 * @param {Date} sysTime 当前系统时间
 * @param {Number} remainStampCount 用户当前剩余印花数量
 */
const resloveCouponState = (coupon, execAct, sysTime, remainStampCount: number) => {
  let status = CouponState.got;
  const hasGot = coupon?.hasGot || 0;
  // totalNum 兑换活动最大参与总次数（所有用户）
  // totalSendNum 兑换活动已参与总次数（所有用户）
  // userLimit 用户最大领取次数
  const { totalNum, totalSendNum, userLimit, userMonthLimit, userMonthSent } = execAct;

  // 平台当日兑换上限次数
  const dayLimit = execAct.dayLimit || 99999999;
  // 平台当日已兑换次数
  const daySent = execAct.daySent || 0;
  if (userMonthSent >= userMonthLimit || hasGot >= userLimit) {
    // 超限-判断个人月限额与个人月已领次数 或 个人领取已达上限（保持已有线上的逻辑）
    status = CouponState.over;
  } else if (execAct.endTime < sysTime) {
    // 过期
    status = CouponState.expired;
  } else if (totalSendNum >= totalNum) {
    // 已兑完（针对《非日限额》的券）
    status = CouponState.end;
  } else if (daySent >= dayLimit) {
    // 已达平台当日派发上限(针对《日限额》的券)
    status = CouponState.pover;
  } else if (execAct.qualCnt > remainStampCount) {
    // 印花不足
    status = CouponState.unable;
  } else {
    // 可领取
    status = CouponState.enable;
  }
  return status;
};

// 获取时间类tag配置
const getCouponTimeTag = (coupon) => {
  let text = '';
  // 不下架 或者 当前时间小于tag下架时间
  if (!coupon.tagEndTime || dayjs().isBefore(dayjs(coupon.tagEndTime))) {
    if (coupon.tagKey) {
      text = TAG_CONF[coupon.tagKey.toLowerCase()] || coupon.tagKey;
    }
  }
  return text;
};

/**
 * 获取券左上角配置
 *
 * 1. 如果是「限时抢」券的锁定状态，则展示限时抢文案
 * 2. 如果不是，则展示配置的文案
 *
 * @param coupon
 * @param isLock
 * @returns
 */
const getCouponTag = (coupon, isLock: boolean) => {
  if (isLock) return i18n('12:00可兌換');
  return getCouponTimeTag(coupon);
};

// 获取库存文案
const getRemainText = (coupon, isLock: boolean) => {
  const text = '';
  if (isLock) return text;
  // 只有日限额才展示库存
  if (coupon.isDayLimitCoupon) {
    return i18n('剩餘x%可領', { availablePercent: coupon.availablePercent });
  }
  return text;
};

/**
 * 根据券获取视图配置
 *
 * over > unable > pover > enable
 *
 * end 没有
 * 非日限额消耗： 某一优惠券总库存消耗完（非日限额消耗完）时，该优惠券下线，不在页面显示
 * 日限额： 没有已兑罄，我们一般会每日均摊，库存消耗完的那一天就是优惠券下线的时间，所以没有已兑罄状态(日限额当天就是pover，最后是end)
 *
 * expired 没有
 *
 * lock 没有
 *
 * @param coupon 券
 */
const getViewConfByCoupon = (coupon: any, isLock: boolean): ViewCouponConf => {
  if (coupon.couponState === CouponState.over) {
    // 个人限额
    return {
      // 按钮
      isShowButton: true,
      buttonType: CouponButtonType.disabled,
      couponBtnText: i18n('x個兌', { exQualCnt: coupon.exQualCnt }),

      // 左上角
      couponTagText: '',
      couponTagColor: CouponTagColor.orange, // 优惠券标签颜色

      // 右上角
      isShowStamp: false, // 是否展示邮戳
      stampText: '', // 邮戳文本
      // 右下角
      tipsTextRight: i18n('兌換已達上限'), // 优惠券提示条文本 - 右侧
    };
  }
  if (coupon.couponState === CouponState.unable) {
    // 印花不足
    return {
      // 按钮
      isShowButton: true,
      buttonType: CouponButtonType.default,
      couponBtnText: i18n('x個兌', { exQualCnt: coupon.exQualCnt }),

      // 左上角
      couponTagText: getCouponTag(coupon, isLock),
      couponTagColor: CouponTagColor.orange, // 优惠券标签颜色

      // 右上角
      isShowStamp: false, // 是否展示邮戳
      stampText: '', // 邮戳文本
      // 右下角
      tipsTextRight: getRemainText(coupon, isLock), // 优惠券提示条文本 - 右侧
    };
  }
  // 状态优先级：12点可兑 > 今日已兑完
  if (!isLock && coupon.couponState === CouponState.pover) {
    // 平台当日限额
    return {
      // 按钮
      isShowButton: true,
      buttonType: CouponButtonType.disabled,
      couponBtnText: i18n('x個兌', { exQualCnt: coupon.exQualCnt }),

      // 左上角
      couponTagText: '',
      couponTagColor: CouponTagColor.orange, // 优惠券标签颜色

      // 右上角
      isShowStamp: false, // 是否展示邮戳
      stampText: '', // 邮戳文本
      // 右下角
      tipsTextRight: i18n('今日獎賞已兌完'), // 优惠券提示条文本 - 右侧
    };
  }
  // 可领取
  return {
    // 按钮
    isShowButton: true,
    buttonType: CouponButtonType.default,
    couponBtnText: i18n('x個兌', { exQualCnt: coupon.exQualCnt }),

    // 左上角
    couponTagText: getCouponTag(coupon, isLock),
    couponTagColor: CouponTagColor.orange, // 优惠券标签颜色

    // 右上角
    isShowStamp: false, // 是否展示邮戳
    stampText: '', // 邮戳文本
    // 右下角
    tipsTextRight: getRemainText(coupon, isLock), // 优惠券提示条文本 - 右侧
  };
};

/** 是否为多券 */
const isMultiCoupon = (coupon: ExActAndConfigCouponInfo): boolean => coupon.perCount > 1;

/** 获取有效期限 */
const getValidPeriod = (coupon: ExActAndConfigCouponInfo): string => {
  const { validExpireType, validTime, expireTime, expireDay } = coupon;
  if (validExpireType === ExpireType.FIXED) {
    return `${dayjs(validTime).format('YYYY.MM.DD')} - ${dayjs(expireTime).format('YYYY.MM.DD')}`;
  }
  return i18n('領取後x天内有效', { expireDay });
};

/** 获取优惠券描述 */
const getPrizeDesc = (coupon: ExActAndConfigCouponInfo): string => {
  const { requiredNum, perCount, discount, isSpCoupon } = coupon;
  return isMultiCoupon(coupon) || isSpCoupon ? i18n('含n張HK$x優惠券', { perCount, discount }) : i18n('消費滿HK$x可用', { requiredNum });
};

/** 商户展示券的展示信息 */
const getSpCouponInfo = () => {
  const info = {
    tipsType: CouponTipsType.manual,
    tipsText: i18n('須手動啟用'),
  };
  return info;
};

/**
 * 是否线上使用的商户展示券
 * 类型1-前往付款： customize_jump_name和customize_jump_url都为“前往付款”
 * 类型2-自定义跳转：两个字段不为空
 * 类型3-无跳转：两个字段都为空
 */
const isOnlineMerchantCoupon = (coupon: ExActAndConfigCouponInfo) => {
  const { customizeJumpName, customizeJumpUrl } = coupon;
  return customizeJumpName !== customizeJumpUrl;
};

export {
  isMultiCoupon,
  isDayLimitCoupon,
  resloveCouponState,
  isOnlineMerchantCoupon,
  getAvailablePercent,
  getViewConfByCoupon,
  checkIsSpCoupon,
  getFormattedDiscount,
  checkIsDiscountCoupon,
  getPrizeDesc,
  getSpCouponInfo,
  getValidPeriod,
};
